/**
 * API client for Tier 2 Feedback Loop endpoints.
 *
 * Coordinator feedback on after-action reviews (AAR feedback).
 * Endpoints handle CRUD operations on the `aar_feedback` table, plus the
 * bulk-apply operation that stamps `applied_at` on pending rows.
 *
 * Session 5.12 (2026-04-16): replaces the Phase A 501 stubs with real calls.
 */

import client from './client';

/**
 * AARFeedback — Coordinator feedback on an after-action review.
 * Mirrors backend AARFeedbackRead / AARFeedbackListItem schema.
 */
export interface AARFeedback {
  id: number;
  tenant_id: number;
  aar_id: number | null;
  coordinator_id: number | null;
  feedback_tag: string;
  reason: string | null;
  created_at: string; // ISO datetime
  applied_at: string | null; // ISO datetime
}

/**
 * CreateAARFeedbackRequest — Request body for submitting new feedback.
 * Mirrors backend AARFeedbackCreate schema.
 */
export interface CreateAARFeedbackRequest {
  feedback_tag: string; // One of: high_quality, mixed, low_quality, too_soft, great_insights
  reason?: string;
}

/**
 * AARFeedbackResponse — Response from POST /canvass/aars/{id}/feedback.
 * Mirrors backend AARFeedbackResponse schema.
 */
export interface AARFeedbackResponse {
  id: number;
  aar_id: number | null;
  feedback_tag: string;
  created_at: string;
  recompute_scheduled_at: string | null;
}

/**
 * BulkApplyRequest / BulkApplyResponse — the mark-applied bulk operation.
 */
export interface BulkApplyRequest {
  ids: number[];
}

export interface BulkApplyResponse {
  updated_count: number;
  applied_at: string;
}

/**
 * ListAARFeedbackResponse — Response envelope from GET /canvass/aars/feedback.
 * Matches the shared `ListEnvelope[T]` shape used by the other paginated
 * list endpoints (the `useListQuery` hook expects this exact shape).
 */
export interface ListAARFeedbackResponse {
  items: AARFeedback[];
  page: number;
  page_size: number;
  total: number;
  sort: string | null;
  order: 'asc' | 'desc' | null;
  // Backend returns a dict<str, Any>; the frontend hook is written against a
  // list of chips but tolerates both shapes. Keep the backend shape here and
  // let the hook adapt.
  applied_filters: Record<string, unknown> | Array<{ key: string; label: string; value: string }>;
}

// ============================================================================
// Feedback API methods
// ============================================================================

/**
 * Get paginated list of AAR feedback entries for the coordinator's tenant.
 *
 * Query parameters (passed via params object):
 *   - page: number (default 1)
 *   - page_size: number (default 50)
 *   - feedback_tag: string (filter by tag)
 *   - applied: 'true' | 'false' (filter by applied_at NOT NULL / NULL)
 *   - created_at__gte: string (ISO date, filter by created date >=)
 *   - created_at__lte: string (ISO date, filter by created date <=)
 *   - q: string (search in reason text)
 *   - sort: string (field to sort by, e.g. 'created_at')
 *   - order: 'asc' | 'desc'
 */
export async function listAARFeedback(params?: Record<string, unknown>) {
  const response = await client.get<ListAARFeedbackResponse>(
    '/canvass/aars/feedback',
    { params }
  );
  return response.data;
}

/**
 * Get a single AAR feedback entry by ID.
 *
 * NOTE: this endpoint is not implemented on the backend yet (Phase 2). The
 * current UI does not need it — the list view carries everything the
 * coordinator sees. Left exported in case a follow-up view wants it.
 */
export async function getAARFeedback(feedbackId: number) {
  const response = await client.get<AARFeedback>(
    `/canvass/aars/feedback/${feedbackId}`
  );
  return response.data;
}

/**
 * Submit feedback on an after-action review.
 *
 * Args:
 *   aarId: ID of the AAR being reviewed.
 *   body: { feedback_tag, reason? }
 *
 * Returns:
 *   { id, aar_id, feedback_tag, created_at, recompute_scheduled_at? }
 *
 * Throws:
 *   400 — invalid feedback_tag
 *   404 — AAR not found in tenant
 *   403 — caller is not coordinator+
 */
export async function submitAARFeedback(
  aarId: number,
  body: CreateAARFeedbackRequest
): Promise<AARFeedbackResponse> {
  const response = await client.post<AARFeedbackResponse>(
    `/canvass/aars/${aarId}/feedback`,
    body
  );
  return response.data;
}

/**
 * Bulk-mark feedback rows as applied. Rows must belong to the caller's
 * tenant; unknown/already-applied rows are silently skipped.
 */
export async function bulkApplyFeedback(
  ids: number[]
): Promise<BulkApplyResponse> {
  const response = await client.post<BulkApplyResponse>(
    '/canvass/aars/feedback/bulk-apply',
    { ids }
  );
  return response.data;
}

/**
 * Feedback tag options (controlled vocabulary).
 * Keep in sync with backend ALLOWED_FEEDBACK_TAGS in
 * services/feedback/service.py.
 */
export const FEEDBACK_TAG_OPTIONS = [
  { value: 'high_quality', label: 'High Quality' },
  { value: 'mixed', label: 'Mixed' },
  { value: 'low_quality', label: 'Low Quality' },
  { value: 'too_soft', label: 'Too Soft' },
  { value: 'great_insights', label: 'Great Insights' },
];

/**
 * Tags that are eligible for auto-apply by the replay cron.
 * `mixed` is excluded — it's a neutral signal the coordinator wants visible
 * but shouldn't propagate downstream.
 */
export const AUTO_APPLY_TAGS: ReadonlySet<string> = new Set([
  'high_quality',
  'low_quality',
  'too_soft',
  'great_insights',
]);

/**
 * Get a human-readable label for a feedback tag.
 */
export function getFeedbackTagLabel(tag: string): string {
  const option = FEEDBACK_TAG_OPTIONS.find((o) => o.value === tag);
  return option ? option.label : tag;
}

/**
 * Color palette for feedback tags (for badge styling).
 */
export const FEEDBACK_TAG_COLORS: Record<string, string> = {
  high_quality: 'bg-green-900/30 border-green-600 text-green-200',
  mixed: 'bg-yellow-900/30 border-yellow-600 text-yellow-200',
  low_quality: 'bg-red-900/30 border-red-600 text-red-200',
  too_soft: 'bg-orange-900/30 border-orange-600 text-orange-200',
  great_insights: 'bg-blue-900/30 border-blue-600 text-blue-200',
};

/**
 * Get a Tailwind color class for a feedback tag.
 */
export function getFeedbackTagColor(tag: string): string {
  return (
    FEEDBACK_TAG_COLORS[tag] ||
    'bg-slate-900/30 border-slate-600 text-slate-200'
  );
}
